// export default PostOptionsModal;
import React from "react";
import {
  X,
  Trash2,
  Heart,
  AlarmClock,
  Bookmark,
  CheckCircle,
  Layers,
  Flag
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const styles = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  .animate-slide-up {
    animation: slide-up 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("post-options-modal-styles")
) {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  styleSheet.id = "post-options-modal-styles";
  document.head.appendChild(styleSheet);
}

const PostOptionsModal = ({
  post,
  onClose,
  onDelete,
  onReport,      
  isOwner = false,
  isDeleting = false,
}) => {
  const { user } = useAuth();

  const collectionOptions = [
    {
      id: "favourite",
      icon: Heart,
      label: "Favourite",
      description: "You added this post to your favorites",
    },
    {
      id: "read-later",
      icon: AlarmClock,
      label: "Read Later",
      description: "You saved this post to read later",
    },
    {
      id: "save",
      icon: Bookmark,
      label: "Save",
      description: "You saved this post to your collection",
    },
  ];

  // Get the active collection type from the post
  const activeCollection = post?.collectionType || null;

  // Find the collection option that matches the active collection
  const activeCollectionOption = activeCollection
    ? collectionOptions.find((option) => option.id === activeCollection)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-2xl max-h-[60vh] flex flex-col animate-slide-up px-4 sm:px-0 sm:max-w-xl sm:mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Post Options
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Collection Status - Only show if post is in a collection */}
            {activeCollectionOption && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 font-medium">
                  IN YOUR COLLECTION
                </p>
                <div className="flex items-center justify-between p-2 rounded-lg">
                  {/* Left side: Icon and text */}
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 rounded-full border border-gray-300 bg-white">
                      <activeCollectionOption.icon
                        className="w-4 h-4 text-gray-900"
                        fill={
                          activeCollectionOption.id === "favourite"
                            ? "currentColor"
                            : "none"
                        }
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {activeCollectionOption.label}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {activeCollectionOption.description}
                      </span>
                    </div>
                  </div>

                  {/* Right side: Checkmark */}
                  <div className="p-1 rounded-full border border-gray-300 bg-white">
                    <CheckCircle className="w-4 h-4 text-gray-900" />
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons - Only show if user owns the post */}
            {isOwner && (
              <div
                className={`space-y-2 ${
                  activeCollectionOption
                    ? "pt-4 border-t border-gray-200 dark:border-gray-800"
                    : ""
                }`}
              >
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
                  POST ACTIONS
                </p>
                <button
                  onClick={() => {
                    if (post && !isDeleting) {
                      onDelete(post.id);
                    }
                  }}
                  disabled={isDeleting}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="p-2 rounded-full border border-gray-300 bg-white">
                    {isDeleting ? (
                      <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Trash2 className="w-4 h-4 text-gray-900" />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">
                      {isDeleting ? "Deleting..." : "Delete Post"}
                    </span>
                    <span className="text-xs text-red-500 dark:text-red-400">
                      Permanently remove this post
                    </span>
                  </div>
                </button>
              </div>
            )}

            {/* If no collections and not owner, show empty state */}
            {!activeCollectionOption && !isOwner && (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-12 h-12 rounded-full border border-gray-300 bg-white flex items-center justify-center mb-3">
                  <Layers className="w-6 h-6 text-gray-900" />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Organize your content
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
                  Add this post to collections to easily find it later and keep
                  your favorite content organized
                </p>
              </div>
            )}

            {/* If no collections but user owns the post, show different empty state */}
            {!activeCollectionOption && isOwner && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full border border-gray-300 bg-white flex items-center justify-center mb-3">
                  <Layers className="w-6 h-6 text-gray-900" />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Manage your post
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
                  Delete this post, or add it to collections from the
                  post menu
                </p>
              </div>
            )}

            {/* Report Action - only for posts that aren't your own */}
            {!isOwner && (
              <>
                <hr className="mx-2 border-t border-gray-200 dark:border-gray-800" />
                <div>
                  <button
                    onClick={onReport}
                    className="w-full sm:p-3 text-center rounded-lg font-semibold text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    Report
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostOptionsModal;
